import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, message, QRCode } from 'antd';
import { AlipayCircleFilled, WechatFilled } from '@ant-design/icons';
import { BsCheckCircleFill, BsCircle, BsInfo } from "react-icons/bs";
import httpClient from '../utils/HttpClient';
import { ORDERS_URL } from '../constants/Url';
import { useDispatch } from 'react-redux';
import { fetchOrdersListRequest } from '../modules/shop/actions';
import { useTranslation } from 'react-i18next';

const PaymentModal = ({ open, onCancel, order }) => {
  const [paymentMethod, setPaymentMethod] = useState('alipay');
  const [paying, setPaying] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const runTimer = useRef(null);
  const timer = useRef(null);
  const [paymentQrcode, setPaymentQrcode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [checkPaymentStatusParam, setCheckPaymentStatusParam] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  useEffect(() => {
    return () => {
      setPaymentQrcode(null);
      setErrorMessage(null);
      setCheckPaymentStatusParam(null);
      runTimer.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    }
  }, []);

  const handleClose = () => {
    setPaymentQrcode(null);
    setErrorMessage(null);
    setCheckPaymentStatusParam(null);
    runTimer.current = false;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (onCancel) onCancel();
  };

  const checkPaymentStatus = async (orderId, method, payUrl) => {
    if (!runTimer.current) return;
    setErrorMessage(null);
    try {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = null;
      const res = await httpClient.post(`${ORDERS_URL}/payment-query`, {
        method: method,
        orderId: orderId
      });
      if (res.data && res.data.status === 200) {
        const order = res.data.data || res.data;
        if (order.paymentStatus === "paid") {
          // Call payment API to mark as paid
          runTimer.current = false;
          if (onCancel) onCancel();

          message.success(t('text_payment_request_success'));
          dispatch(fetchOrdersListRequest());
          
          return;
        }
      } else {
        runTimer.current = false;
        setErrorMessage(t('text_failed_loading_order_detail'));
      }
      timer.current = setTimeout(() => {
        checkPaymentStatus(orderId, method, payUrl);
      }, 3000);
    } catch (err) {
      runTimer.current = false;
      console.error('Failed to check order status:', err);
      setErrorMessage(err.message || 'Error checking order status');
    }
  };

  const handleConfirm = async () => {
    if (!order) return;
    setPaying(true);
    try {
      const res = await httpClient.post(`${ORDERS_URL}/pay-now`, {
        orderId: order.orderId || order.id,
        method: paymentMethod,
        currency: "CNY"
      });
      if (res.data && (res.data.status === 200 || (res.data.status === undefined && (res.status === 200 || res.status === 201)))) {
        var neworder = res.data.data || res.data;

        var code_url = null;
        if (paymentMethod === 'alipay') {
          code_url = neworder?.orderinfo?.qrCode;
        } else if (paymentMethod === 'wechatpay') {
          code_url = neworder?.orderinfo?.code_url;
        }
        setPaymentQrcode(code_url);

        const payUrl = neworder.paymentUrl || neworder.payUrl;

        if (payUrl) {
          if (onCancel) onCancel();
          window.location.href = payUrl;
          return;
        }

        if (code_url) {
          runTimer.current = true;
          setCheckPaymentStatusParam({
            orderId: neworder.orderId,
            method: paymentMethod,
            payUrl: payUrl
          });
          checkPaymentStatus(neworder.orderId, paymentMethod, payUrl);
        }
      } else {
        message.error(t(res.data?.message || 'text_payment_failed'));
      }
    } catch (error) {
      console.error('Error paying order:', error);
      message.error(t(error?.message || error?.data?.message || 'text_payment_failed'));
    } finally {
      setPaying(false);
    }
  };

  return (
    <Modal
      title={<div className="text-center font-semibold text-base text-gray-800">{paymentQrcode ? t("text_tobepaid_qrcode") : t("text_select_payment")}</div>}
      open={open}
      onCancel={() => {
        if (paymentQrcode) {
          setShowCloseModal(true);
        } else {
          handleClose();
        }
      }}
      footer={paymentQrcode ? null : (
        <Button
          type="primary"
          block
          size="large"
          loading={paying}
          onClick={handleConfirm}
          className="bg-[#e80d44] hover:bg-[#d00b3c] border-none mt-4 rounded-xl font-bold h-12 text-[15px]"
        >
          {t("text_confirm_payment")}
        </Button>
      )}
      closable={true}
      width={400}
      centered
      styles={{ body: { padding: paymentQrcode ? '24px' : '24px 0', borderRadius: '16px' }, header: { textAlign: 'center', marginBottom: 0 } }}
    >
      <div className="px-5">
        {paymentQrcode ? (
          <>
          <div className="flex flex-col items-center justify-center py-4">
            <QRCode
              value={paymentQrcode}
              size={220}
              icon={paymentMethod === 'wechatpay' ? "https://ts4.tc.mm.bing.net/th/id/ODF.BvtHqZTl6qLypPDIASUGoA?w=32&h=32&qlt=97&pcl=fffffa&o=6&pid=1.2" : "https://ts1.tc.mm.bing.net/th/id/ODF.tFQwCWKfvMeJYOFxHknauw?w=32&h=32&qlt=90&pcl=fffffa&o=6&pid=1.2"}
              iconSize={{
                width: 32,
                height: 32,
              }}
            />
          </div>
          <div style={{marginTop: 10, marginBottom: errorMessage ? 10 : 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5}}>
            <div className="bg-[#fff0f4] p-0.5 rounded-full">
              <BsInfo className="text-[#e80d44] text-xl shrink-0" />
            </div>
            <span className="text-[#e80d44] text-sm font-medium">{t("text_qrcode_desc")}</span>
          </div>
          {errorMessage ? <div className="flex flex-col items-center justify-center w-full">
            <span style={{color: 'red'}} className="text-sm text-gray-500 font-medium">{errorMessage}</span>

            <button
              onClick={() => {
                checkPaymentStatus(checkPaymentStatusParam.orderId, checkPaymentStatusParam.method, checkPaymentStatusParam.payUrl);
              }}
              style={{width: '100%'}}
              className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 hover:scale-105 active:scale-95`}
            >
              {t("text_retry")}
            </button>
          </div> : null}
          </>
        ) : (
          <div className="bg-white rounded-2xl px-5 py-2 shadow-sm border border-gray-100">
            <div
              onClick={() => setPaymentMethod('alipay')}
              className="flex items-center justify-between py-4 border-b border-gray-50 cursor-pointer group hover:bg-gray-50/50 transition-colors -mx-5 px-5 first:rounded-t-2xl"
            >
              <div className="flex items-center gap-4">
                <AlipayCircleFilled className="text-[#1677ff] text-[28px]" />
                <span className="text-[15px] text-gray-800 font-medium tracking-wide">{t("text_alipay_payment")}</span>
              </div>
              {paymentMethod === 'alipay' ? (
                <BsCheckCircleFill className="text-[#e2005a] text-[22px] shadow-sm rounded-full" />
              ) : (
                <BsCircle className="text-gray-200 text-[22px] group-hover:border-gray-300 transition-colors" />
              )}
            </div>

            <div
              onClick={() => setPaymentMethod('wechatpay')}
              className="flex items-center justify-between py-4 cursor-pointer group hover:bg-gray-50/50 transition-colors -mx-5 px-5 last:rounded-b-2xl"
            >
              <div className="flex items-center gap-4">
                <WechatFilled className="text-[#09b83e] text-[28px]" />
                <span className="text-[15px] text-gray-800 font-medium tracking-wide">{t("text_wechat_payment")}</span>
              </div>
              {paymentMethod === 'wechatpay' ? (
                <BsCheckCircleFill className="text-[#e2005a] text-[22px] shadow-sm rounded-full" />
              ) : (
                <BsCircle className="text-gray-200 text-[22px] group-hover:border-gray-300 transition-colors" />
              )}
            </div>
          </div>
        )}
      </div>
      <Modal
        open={showCloseModal}
        onCancel={() => {
          setShowCloseModal(false);
        }}
        closeable={true}
        footer={null}
        centered
        width={400}
        title={null}
        styles={{
          body: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 16px 32px 16px',
          }
        }}
      >
        <div className="flex flex-col items-center justify-center p-2">
          <div style={{padding: 12}} className="bg-[#fff0f4] rounded-full">
            <BsInfo size={36} color="#e80d44" />
          </div>
          <span className="text-sm shrink-0 font-medium mt-4">{t("text_qrcode_warning")}</span>
          <button
            onClick={() => {
              setShowCloseModal(false);
              setTimeout(() => {
                handleClose();
              }, 100);
            }}
            style={{width: '100%'}}
            className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 hover:scale-105 active:scale-95`}
          >
            {t("text_qrcode_confirm")}
          </button>
        </div>
      </Modal>
    </Modal>
  );
};

export default PaymentModal;
